import { useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { Compass } from 'lucide-react'
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google'
import { useTranslation } from 'react-i18next'
import { useAuth } from './useAuth'
import { Spinner } from '../../shared/components/Spinner'

const GOOGLE_CLIENT_ID: string = import.meta.env.VITE_GOOGLE_CLIENT_ID ?? ''

export function Login() {
  const { status, signInWithGoogle } = useAuth()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const from = (location.state as { from?: Location })?.from?.pathname ?? '/'

  if (status === 'authenticated') {
    return <Navigate to={from} replace />
  }

  async function completeSignIn(idToken?: string) {
    setBusy(true)
    setError(null)
    try {
      await signInWithGoogle(idToken)
      navigate(from, { replace: true })
    } catch {
      setError(t('auth.sign_in_failed'))
    } finally {
      setBusy(false)
    }
  }

  async function handleSignIn() {
    await completeSignIn()
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-st-bg px-6">
      <div className="w-full max-w-sm rounded-st-lg border border-st-line bg-st-surface p-8 shadow-sm">
        <div className="mb-6 flex flex-col items-center text-center">
          <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-st-lg bg-st-primary text-white">
            <Compass className="h-7 w-7" />
          </span>
          <h1 className="font-display text-2xl font-bold text-st-text">{t('auth.product_name')}</h1>
          <p className="mt-1 text-sm text-st-muted">
            {t('auth.product_tagline')}
          </p>
        </div>

        {GOOGLE_CLIENT_ID ? (
          <div className="flex flex-col items-center gap-3">
            {busy ? (
              <Spinner label={t('auth.signing_in')} />
            ) : (
              <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
                <GoogleLogin
                  onSuccess={(cred) => void completeSignIn(cred.credential)}
                  onError={() => setError(t('auth.google_sign_in_failed'))}
                  useOneTap
                />
              </GoogleOAuthProvider>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={handleSignIn}
            disabled={busy}
            className="flex min-h-12 w-full items-center justify-center gap-3 rounded-st-md border border-st-line bg-st-surface px-4 font-medium text-st-text transition hover:bg-st-surface-2 disabled:opacity-60"
          >
            {busy ? (
              <Spinner label={t('auth.signing_in')} />
            ) : (
              <>
                <GoogleIcon />
                {t('auth.continue_demo')}
              </>
            )}
          </button>
        )}

        {error && <p className="mt-3 text-center text-sm text-st-danger">{error}</p>}

        <p className="mt-4 text-center text-xs text-st-muted">
          {GOOGLE_CLIENT_ID
            ? t('auth.google_hint')
            : t('auth.demo_hint')}
        </p>
      </div>
    </div>
  )
}

function GoogleIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38Z"
      />
    </svg>
  )
}
